import { ReactNode, useMemo, useRef } from "react";
import { usePopupPortal } from "../../../../contexts/PopupContext";
import { setTrackingOp, state, useTrackingOp } from "../../../../contexts/TrackingOpContext";
import InfoFish from "../../../../pages/PlanOperationPage/Popup/EditOp/_components/InfoFish";
import { EXECUTION_TYPE, OP_RUNNING_MODE } from "../../../../util/varibles/constants";
import { cloneObj, firstLetter } from "../../../../util/varibles/global";
import { IActivityLog, IHarvest2 } from "../../../../util/varibles/interface";
import Icon, { ICON_NAME } from "../../../Icon";
import FinishStepButton from "../FinishStepButton";
import NextTabButton from "../NextTabButton";
import DeliveryType from "./DeliveryType";
import { finishDischarging, startDischarging } from "./DischargingPopup";
import Tanks from "./Tanks";

const NextTab = ({ isDone }: { isDone: boolean }) => {
    const set = setTrackingOp();

    return <>
        {isDone && <NextTabButton
            name={"Finish discharging"}
            onClick={() => {
                set(({ step }) => ({ step: step + 1 }))
            }}
        />}
    </>
}

export interface IDischarge {
    name: string
    total_weight: number
    fish_amount: number
    delivery_type_id?: string
    group_id?: string
    real_start_time?: number
    real_finish_time?: number
    tank_id?: string
}

export default function Discharging({ log, prev, harvesting, stepIndex }: { log: IActivityLog, prev: IActivityLog, harvesting?: IActivityLog, stepIndex: number }) {
    const { openPopup, popupHolder } = usePopupPortal();
    const [op] = useTrackingOp(state => state.op?.operation);
    const { op_running_mode = OP_RUNNING_MODE.NORMAL, execution_type, sub_operations = [], dead_fish: operationDeadFish, tanks = [], delivery_types = [] } = op as IHarvest2 || {};
    const infoRef = useRef({ execution_type, delivery_types, stepIndex });
    const { sub_steps = [], real_sub_steps = [], real_start_time, real_finish_time } = log;
    const { real_start_time: prevTime } = prev;
    const { real_sub_steps: harvestingSubSteps = [] } = harvesting || {};
    const deadFish = operationDeadFish ?? real_sub_steps.reduce((total, item) => total + (item.dead_fish || 0), 0);

    const remainingByTank = useMemo(() => {
        const result = harvestingSubSteps.reduce((rs: Record<string, {
            fish_amount: number
            total_weight: number
        }>, item) => {
            const current = rs[item.tank_id] || { fish_amount: 0, total_weight: 0 };
            current.fish_amount += item.fish_amount || 0;
            current.total_weight += item.total_weight || 0;
            rs[item.tank_id] = current;
            return rs;
        }, {});

        real_sub_steps.forEach(item => {
            if (!item.real_finish_time)
                return;

            const current = result[item.tank_id] || { fish_amount: 0, total_weight: 0 };
            current.fish_amount = Math.max(
                0,
                current.fish_amount - (item.fish_amount || 0) - (item.dead_fish || 0)
            );
            current.total_weight = Math.max(0, current.total_weight - (item.total_weight || 0));
            result[item.tank_id] = current;
        });

        return result;
    }, [harvestingSubSteps, real_sub_steps]);

    const isDone = useMemo(() => {
        if (execution_type === EXECUTION_TYPE.AUTO)
            return true;

        if (op_running_mode !== OP_RUNNING_MODE.DETAIL)
            return !!real_finish_time;

        const tankIds = Object.keys(remainingByTank);
        return tankIds.length > 0 && tankIds.every(tankId => remainingByTank[tankId].fish_amount <= 0);
    }, [execution_type, op_running_mode, real_finish_time, remainingByTank]);

    const deliveriesRef = useRef(sub_operations.reduce((rs: { [id: string]: IDischarge }, item) => {
        const { delivery_types = [] } = item;
        delivery_types?.forEach(sub => {
            const { fish_amount = 0, total_weight = 0 } = sub;
            if (rs[sub.id]) {
                rs[sub.id].fish_amount += fish_amount;
                rs[sub.id].total_weight += total_weight;
            } else {
                rs[sub.id] = { name: sub.name, fish_amount, total_weight }
            }
        })
        return rs;
    }, {}));

    const { actualTotal, actualByTank } = useMemo(() => {
        const total: { [deliveryId: string]: { name: string, total_weight: number, fish_amount: number } } = {}
        const tanks: { [tankId: string]: { [deliveryId: string]: IDischarge } } = {};
        real_sub_steps.forEach(item => {
            const { delivery_type_id = "", delivery_type_name = delivery_type_id, tank_id, fish_amount = 0, total_weight = 0 } = item
            const { name = delivery_type_name } = deliveriesRef.current[delivery_type_id] || {};
            const convertedName = firstLetter(name.replace(/_/g, ' '));
            if (total[delivery_type_id]) {
                total[delivery_type_id].fish_amount += fish_amount;
                total[delivery_type_id].total_weight += total_weight;
            } else {
                total[delivery_type_id] = {
                    name: convertedName,
                    fish_amount,
                    total_weight
                }
            }
            tanks[tank_id] = { ...tanks[tank_id], [delivery_type_id]: { ...item, fish_amount, total_weight, name: convertedName } }
        })
        return { actualTotal: total, actualByTank: tanks }
    }, [real_sub_steps])

    const discharge = useMemo(() => {
        if (op_running_mode !== OP_RUNNING_MODE.DETAIL)
            return {};

        const temp: typeof actualByTank = cloneObj(actualByTank)
        const result = sub_steps.reduce((rs: { [tankId: string]: { [deliveryId: string]: IDischarge } }, item) => {
            const { delivery_type_id = "", tank_id } = item;
            const { name = delivery_type_id } = deliveriesRef.current[delivery_type_id] || {};
            const convertedName = firstLetter(name.replace(/_/g, ' '));
            if (temp[tank_id]?.[delivery_type_id]) {
                const { [delivery_type_id]: current, ...args } = temp[tank_id];
                temp[tank_id] = args;
                rs[tank_id] = { ...rs[tank_id], [delivery_type_id]: { ...current, name: convertedName } }
            } else {
                const { total_weight = 0, fish_amount = 0 } = item;
                rs[tank_id] = { ...rs[tank_id], [delivery_type_id]: { ...item, name: convertedName, total_weight, fish_amount } }
            }
            return rs;
        }, {});

        Object.keys(temp).forEach(tankId => {
            const deliveryTypes = temp[tankId];
            Object.keys(deliveryTypes).forEach(deliveryId => {
                const { name = deliveryId } = deliveriesRef.current[deliveryId] || {};
                const convertedName = firstLetter(name.replace(/_/g, ' '));
                result[tankId] = { ...result[tankId], [deliveryId]: { ...deliveryTypes[deliveryId], name: convertedName } }
            })
        })

        return tanks.reduce((rs: Record<string, ReactNode>, item) => {
            const deliveryTypes = result[item.id] || {};
            const availableFishAmountOnVessel = Object.values(remainingByTank).reduce((total, tank) =>
                total + tank.fish_amount, 0
            );
            const {
                fish_amount: availableFishAmount = 0,
                total_weight: availableTotalWeightInBaseUnit = 0,
            } = remainingByTank[item.id] || {};
            const availableTotalWeight = availableTotalWeightInBaseUnit / 1000000;
            const disabled = availableFishAmount <= 0;
            rs[item.id] = <div className="flex flex-col gap-3 mt-[20px]">
                {Object.keys(deliveryTypes).map(deliveryId => <DeliveryType
                    key={deliveryId}
                    data={deliveryTypes[deliveryId]}
                    disabled={disabled}
                    onStart={infoRef.current.execution_type !== EXECUTION_TYPE.AUTO ? () => startDischarging({
                        deliveryTypes: infoRef.current.delivery_types,
                        deliveryTypeId: deliveryId,
                        tankId: item.id,
                        status: 0,
                        current_process: infoRef.current.stepIndex
                    }, openPopup) : undefined}
                    onFinish={infoRef.current.execution_type !== EXECUTION_TYPE.AUTO ? () => {
                        const delivery = deliveryTypes[deliveryId];
                        if (!delivery.group_id) {
                            return;
                        }
                        finishDischarging({
                            availableFishAmount,
                            availableFishAmountOnVessel,
                            availableTotalWeight,
                            deliveryTypes: infoRef.current.delivery_types,
                            deliveryTypeId: deliveryId,
                            deliveryTypeName: delivery.name,
                            groupId: delivery.group_id,
                            tankId: item.id,
                            status: 1,
                            current_process: infoRef.current.stepIndex
                        }, openPopup);
                    } : undefined}
                />)}
                {!disabled && infoRef.current.execution_type !== EXECUTION_TYPE.AUTO && <div className="flex justify-center py-[20px]">
                    <div
                        className="flex flex-col items-center cursor-pointer"
                        onClick={() => startDischarging({
                            deliveryTypes: infoRef.current.delivery_types,
                            tankId: item.id,
                            status: 0,
                            current_process: infoRef.current.stepIndex
                        }, openPopup)}
                    >
                        <Icon icon={ICON_NAME.ADD_3} bg={"colorLink"} size={18} />
                        <div className="link text-[14px] mt-[2px]">Select other  delivery methods</div>
                    </div>
                </div>}
            </div>
            return rs
        }, {})
    }, [actualByTank, openPopup, op_running_mode, remainingByTank, sub_steps, tanks])

    const nextStep = useMemo(() => {
        if (!prevTime)
            return null;
        if (execution_type === EXECUTION_TYPE.AUTO)
            return null;
        if (op_running_mode !== OP_RUNNING_MODE.DETAIL) {
            if (real_finish_time)
                return null;

            let name = "Discharging", payload = { group_ids: [], step_state: state[0], current_process: infoRef.current.stepIndex } as { group_ids: string[], step_state: string, step_new?: number, current_process: number };
            if (real_start_time) {
                name = "Finish Discharging";
                payload.step_state = state[1];
                payload.step_new = -1;
            }

            return <FinishStepButton name={name} justify="start" payload={payload} />
        }

        return null;
    }, [execution_type, op_running_mode, prevTime, real_finish_time, real_start_time])

    return <>
        <div className="w-full">
            <div>
                <div className="font-w4 text-[15px]">Discharging info.</div>
                <div className="grid grid-cols-2">
                    <div>
                        {Object.keys(deliveriesRef.current).map(key => {
                            const { name, ...args } = deliveriesRef.current[key];
                            return <div key={key} className="flex gap-1 text-[15px]">
                                <div className="text-comment">Plan discharge {name}:</div>
                                <InfoFish fontSize={15} data={args} />
                            </div>
                        })}
                    </div>
                    <div>
                        {Object.keys(actualTotal).map(key => {
                            const { name, ...args } = actualTotal[key];
                            return <div key={key} className="flex gap-1 text-[15px]">
                                <div className="text-comment">Actual discharge {name}:</div>
                                <InfoFish fontSize={15} data={args} />
                            </div>
                        })}
                        {op_running_mode === OP_RUNNING_MODE.DETAIL && <div className="flex gap-1 text-[15px]">
                            <div className="text-comment">Dead fish:</div>
                            <div>{deadFish || "_ _"}</div>
                        </div>}
                    </div>
                </div>
            </div>
            <div className="mt-3">
                <Tanks infoByTank={discharge} remainingByTank={remainingByTank} showZero />
            </div>
        </div>
        {nextStep}
        <NextTab isDone={isDone} />
        {popupHolder}
    </>
}
